import { RendererParameter } from './models/map.models';
import { NavService } from './../nav/service/nav.service';
import { Component, Inject, OnInit, PLATFORM_ID, inject, DOCUMENT } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { CommonModule, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { Article, ArticleService, ArticleType } from '../admin/shared/public-api';
import { DragDirective } from '../shared/directives/drag.directive';
import { DeviceService } from '../shared/services/device.service';
import { MetaService } from '../shared/services/meta.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  imports: [CommonModule, RouterModule, NgOptimizedImage, DragDirective],
})
export class MapComponent implements OnInit {
  private document = inject(DOCUMENT);
  private readonly articleService = inject(ArticleService);
  private readonly navService = inject(NavService);
  protected readonly deviceService = inject(DeviceService);
  private readonly metaTagService = inject(MetaService);

  canClick = true;
  contentChange$ = new Subject<boolean>();

  map: google.maps.Map;
  options: google.maps.MapOptions;
  markers: google.maps.Marker[] = [];
  articles: Article[];
  filterArticles: Article[];
  showInfo = false;

  markerArticles: { [latLng: string]: Array<Article> } = {};
  selectedMarkerArticles: Array<Article> = [];
  currentMarkerArticle: Article;
  defaultPostition: string;

  renderer: any;

  currentArticleIndex = 0;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    router: Router,
    route: ActivatedRoute,
    titleService: Title
  ) {
    if (route.snapshot.data && route.snapshot.data['name']) {
      titleService.setTitle(route.snapshot.data['title']);

      const baseUrl = environment.baseUrl;
      const description = `Toutes nos explorations sur une seule carte!`;

      this.metaTagService.updateMetaTags(
        description,
        route.snapshot.data['title'],
        `${baseUrl}map`,
        'website',
        `${baseUrl}og_img.png`,
        768,
        432,
        'image/png'
      );
    }
  }

  ngOnInit(): void {
    this.navService.setGreenBackground();

    this.articleService.getAllArticles().subscribe((allArticles: Article[]) => {
      this.articles = allArticles;
      this.filterArticles = allArticles
        .filter((x) => x.articleType !== ArticleType.Setup)
        .map((x) => Object.assign({}, x));
      if (isPlatformBrowser(this.platformId)) {
        this.loadMap();
      }
    });
  }

  private loadMap(): void {
    const loader = new Loader({
      apiKey: 'AIzaSyCPDH8Gnt_6Hg5rd5kE-eqmC7c39z8YfZU',
    });

    loader.load().then(() => {
      import('@googlemaps/markerclusterer').then((markerClusterer) => {
        this.renderer = {
          render: ({ count, position }: RendererParameter, stats: any) => {
            // use d3-interpolateRgb to interpolate between red and blue
            // const color = this.palette(count / stats.clusters.markers.max);
            // create svg url with fill color
            const svg = window.btoa(`
                          <svg fill="#3a5d4d" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
                          <circle cx="120" cy="120" opacity=".9" r="70" />
                          </svg>`);
            // create marker using svg icon
            return new google.maps.Marker({
              position,
              icon: {
                url: `data:image/svg+xml;base64,${svg}`,
                scaledSize: new google.maps.Size(75, 75),
              },
              label: {
                text: String(count),
                color: 'rgba(255,255,255,0.9)',
                fontSize: '12px',
              },
              // adjust zIndex to be above other markers
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            });
          },
        };

        this.map = new google.maps.Map(this.document.getElementById('map') as HTMLElement, {
          center: { lat: 50.927, lng: 4.351 },
          zoom: 8,
          mapId: 'df3d40dd7d920cc4',
          mapTypeId: 'roadmap',
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP,
          },
          fullscreenControl: false,
          gestureHandling: 'greedy',
          streetViewControl: false,
          mapTypeControl: false,
        });

        this.articles.forEach((article) => {
          const marker = new google.maps.Marker({
            position: new google.maps.LatLng(+article.lat, +article.lng),
            title: article.name,
            icon: '/map/marker.png',
          });

          const position = '' + marker.getPosition()?.toString();
          if (
            (article.place === 'Bruxelles' || article.place === 'Brussels') &&
            !this.defaultPostition
          ) {
            this.defaultPostition = position;
            marker.setIcon('/map/selectedMarker.png');
          }

          if (this.markerArticles[position]) {
            this.markerArticles[position].push(article);
            return;
          }

          marker.setMap(this.map);

          this.markerArticles[position] = [article];

          marker.addListener('click', () => {
            this.contentChange$.next(true);
            this.showInfo = true;
            this.markers.map((m) => m.setIcon('/map/marker.png'));
            marker.setIcon('/map/selectedMarker.png');

            this.centerMarker(marker);

            this.selectedMarkerArticles = this.markerArticles[position];
            this.currentMarkerArticle = this.selectedMarkerArticles[0];
          });

          this.markers.push(marker);
        });
        if (!this.defaultPostition) {
          const defaultMarker = this.markers[0];
          const position = '' + defaultMarker.getPosition()?.toString();
          this.defaultPostition = position;
          defaultMarker.setIcon('/map/selectedMarker.png');
        }
        this.selectedMarkerArticles = this.markerArticles[this.defaultPostition];
        new markerClusterer.MarkerClusterer({
          map: this.map,
          markers: this.markers,
          renderer: this.renderer,
        });
      });
    });
  }

  nextArticle(): void {
    const domArticles = this.document.querySelectorAll('.article');
    const currentIndex = this.selectedMarkerArticles.indexOf(this.currentMarkerArticle);
    this.currentMarkerArticle = this.selectedMarkerArticles[currentIndex + 1];
    const translate = (currentIndex + 1) * 100;
    domArticles.forEach((domArticle) => {
      (domArticle as HTMLElement).style.transform = `translateY(-${translate}%)`;
    });
  }

  private centerMarker(marker: google.maps.Marker): void {
    const markerPixelPosition = this.map
      .getProjection()
      ?.fromLatLngToPoint(marker.getPosition() as google.maps.LatLng) as google.maps.Point;

    const bounds = this.map.getBounds() as google.maps.LatLngBounds;
    const center = bounds.getCenter();
    const bottomLeft = bounds.getSouthWest();
    const topRight = bounds.getNorthEast();

    const topRightPixelPosition = this.map
      .getProjection()
      ?.fromLatLngToPoint(topRight) as google.maps.Point;
    const bottomLeftPixelPosition = this.map
      .getProjection()
      ?.fromLatLngToPoint(bottomLeft) as google.maps.Point;
    const centerPixelPosition = this.map
      .getProjection()
      ?.fromLatLngToPoint(center) as google.maps.Point;

    const width = topRightPixelPosition.x - bottomLeftPixelPosition.x;
    const middle = width / 2;
    const targetPosition = topRightPixelPosition.x - middle;
    const diff = targetPosition - markerPixelPosition.x;
    const centerTargetPosition = centerPixelPosition.x - diff;

    const targetLatLng = this.map
      .getProjection()
      ?.fromPointToLatLng(
        new google.maps.Point(centerTargetPosition, markerPixelPosition.y)
      ) as google.maps.LatLng;
    this.map.panTo(targetLatLng);
  }
}
